import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { nanosToMillis } from '@/datetime/dateTime.utilities';
import { TableBuilderColumnType, TableBuilderHeaderType } from '@/tableBuilder/tableBuilder.constants';
import { COLUMNS_AND_STATS } from '@/trendData/trendData.constants';
import { generateTemporaryId } from '@/utilities/utilities';
import { TypeEnum } from '@seeqdev/api/model/ColumnHeaderOutputV1';
import type {
  ColumnOrRowWithDefinitions,
  ConditionTableHeader,
  TableBuilderHeaders,
} from '@/tableBuilder/tableBuilder.types';
import type { CapsuleFormulaTableRow } from '@/utilities/formula.constants';
import type { TableResultOutputV1 } from '@seeqdev/api/model/models';

/**
 * Splits the table-builder columns into the `capsuleProperties` and `conditionProperties` lists
 * that `POST /api/tables/condition` expects. `name` is always requested: each condition metric is
 * faked in the store after fetch (to avoid a second per-metric fetch) and `name` is the minimum
 * property the fake metric needs.
 */
export const buildConditionTableColumnRequestFields = (
  columns: ColumnOrRowWithDefinitions[],
): {
  capsuleProperties: string[];
  conditionProperties: string[];
} => {
  const capsuleProperties = columns
    .filter((column) => column.type === TableBuilderColumnType.CapsuleProperty)
    .map((column) => column.key);
  const conditionProperties = columns
    .filter(
      (column) =>
        column.backingTableColumn?.includes(`${SeeqNames.MaterializedTables.ItemIdColumn}.properties`) &&
        column.type !== TableBuilderColumnType.CapsuleProperty &&
        column.key !== 'ID',
    )
    .map((column) => column.key);

  if (!conditionProperties.includes(SeeqNames.Properties.Name)) {
    conditionProperties.push(SeeqNames.Properties.Name);
  }

  return { capsuleProperties, conditionProperties };
};

export type NormalizedConditionTableHeader = { name: string; type: string; units: string };

export type NormalizedConditionTableData = {
  headers: NormalizedConditionTableHeader[];
  structuredTable: CapsuleFormulaTableRow[];
};

type NormalizeConditionTableResponseOptions = {
  mergeRowsByCapsule?: boolean;
};

type NormalizedConditionTableRow = Partial<CapsuleFormulaTableRow> & {
  [key: string]: unknown;
  ID?: string;
  Start?: unknown;
  End?: unknown;
  cursorKey?: number | null;
  name?: unknown;
  nameExpression?: unknown;
};

const CONDITION_TABLE_ITEM_ID_COLUMN = 'conditionTable_itemId';
const ITEM_PROPERTIES_PREFIX = `${SeeqNames.MaterializedTables.ItemIdColumn}.properties.`;
const UOM_SUFFIX = ' uom';
const PRIORITY_COLOR_SUFFIX = '_color';
const PRIORITY_COLOR_REPLACEMENT = '_priorityColor';
const MERGE_KEY_COLUMNS = [SeeqNames.CapsuleProperties.Start, SeeqNames.CapsuleProperties.End] as const;
const METRIC_DATA_COLUMN_PATTERN = /_(value|priority|priorityColor|priorityName|itemId)$/;
const NAME_ALIAS_KEYS = ['name', 'nameExpression'] as const;

type ConditionTableNormalizationContext = {
  columns: ColumnOrRowWithDefinitions[];
  conditionTableRows: Pick<ConditionTableHeader, 'key'>[];
  headers: TableBuilderHeaders;
};

const isMergeEmpty = (value: unknown): boolean => value == null;

const hasMergeInterval = (row: NormalizedConditionTableRow): boolean => row.Start != null && row.End != null;

const mergeKeyForConditionRow = (row: NormalizedConditionTableRow): string =>
  JSON.stringify(MERGE_KEY_COLUMNS.map((column) => row[column] ?? null));

const rowHasMetricData = (row: NormalizedConditionTableRow): boolean =>
  Object.entries(row).some(([key, value]) => METRIC_DATA_COLUMN_PATTERN.test(key) && !isMergeEmpty(value));

const canMergeConditionRows = (
  target: NormalizedConditionTableRow,
  source: NormalizedConditionTableRow,
  ignoredConflictColumns: Set<string>,
): boolean => {
  const keys = new Set([...Object.keys(target), ...Object.keys(source)]);
  return [...keys].every((key) => {
    if (ignoredConflictColumns.has(key)) {
      return true;
    }

    const targetValue = target[key];
    const sourceValue = source[key];
    return isMergeEmpty(targetValue) || isMergeEmpty(sourceValue) || _.isEqual(targetValue, sourceValue);
  });
};

const mergeConditionRows = (
  target: NormalizedConditionTableRow,
  source: NormalizedConditionTableRow,
): NormalizedConditionTableRow => {
  Object.entries(source).forEach(([key, sourceValue]) => {
    if (isMergeEmpty(target[key]) && !isMergeEmpty(sourceValue)) {
      target[key] = sourceValue;
    }
  });
  return target;
};

const mergeComplementaryConditionRows = (
  rows: CapsuleFormulaTableRow[],
  ignoredConflictColumns: Set<string>,
): NormalizedConditionTableRow[] => {
  const mergedRows: NormalizedConditionTableRow[] = [];
  const rowsByKey = new Map<string, NormalizedConditionTableRow[]>();

  rows.forEach((row) => {
    const normalizedRow = row as NormalizedConditionTableRow;
    if (!hasMergeInterval(normalizedRow)) {
      mergedRows.push(normalizedRow);
      return;
    }

    const key = mergeKeyForConditionRow(normalizedRow);
    const candidates = rowsByKey.get(key) ?? [];
    const mergeTarget = candidates.find((candidate) =>
      canMergeConditionRows(candidate, normalizedRow, ignoredConflictColumns),
    );

    if (mergeTarget) {
      mergeConditionRows(mergeTarget, normalizedRow);
      return;
    }

    candidates.push(normalizedRow);
    rowsByKey.set(key, candidates);
    mergedRows.push(normalizedRow);
  });

  return mergedRows;
};

const compareNullableNumbers = (a: unknown, b: unknown): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return Number(a) - Number(b);
};

const sortConditionRowsForDisplay = (rows: NormalizedConditionTableRow[]): NormalizedConditionTableRow[] =>
  rows
    .map((row, index) => ({ row: row as NormalizedConditionTableRow, index }))
    .sort((a, b) => {
      const startCompare = compareNullableNumbers(a.row.Start, b.row.Start);
      if (startCompare !== 0) return startCompare;

      const endCompare = compareNullableNumbers(a.row.End, b.row.End);
      if (endCompare !== 0) return endCompare;

      const metricCompare = Number(rowHasMetricData(a.row)) - Number(rowHasMetricData(b.row));
      if (metricCompare !== 0) return metricCompare;

      return a.index - b.index;
    })
    .map(({ row }) => row);

const nullableNanosToMillis = (value: unknown): number | null => (value == null ? null : nanosToMillis(Number(value)));

/**
 * Normalizes a `POST /api/tables/condition` response into the shape the table-builder store
 * consumes: UOM sidecar columns are stripped and their values attached as `units` on their
 * base header; backend header names are rewritten to the keys the store expects; rows are
 * turned into objects keyed by the rewritten header names with `startTime` / `endTime`
 * derived from the capsule `Start` / `End` nanos.
 *
 * `statItems` is the list of signal IDs whose per-signal stat columns (e.g. "<id> Maximum")
 * should be renamed to the store's column key.
 */
export const normalizeConditionTableResponse = (
  response: TableResultOutputV1,
  statItems: (string | undefined)[],
  context: ConditionTableNormalizationContext,
  options: NormalizeConditionTableResponseOptions = {},
): NormalizedConditionTableData => {
  const { columns, conditionTableRows, headers } = context;
  const rawHeaders = response.headers ?? [];
  const rawRows = response.rows ?? [];
  const uomColumnIndices = new Set<number>();
  const uomValues = new Map<string, string>();

  rawHeaders.forEach((header, index) => {
    const headerName = header.name ?? '';
    if (headerName.endsWith(UOM_SUFFIX)) {
      const baseColumnName = headerName.replace(UOM_SUFFIX, '');
      uomColumnIndices.add(index);
      const firstRowWithUom = rawRows.find((row) => row[index]);
      const uomValue = firstRowWithUom ? String(firstRowWithUom[index] || '') : '';
      uomValues.set(baseColumnName, uomValue);
    }
  });

  const nonUomRows = rawRows.map((row) => row.filter((_value, index) => !uomColumnIndices.has(index)));
  const columnKeyByBacking = new Map<string, string>();
  columns.forEach((column) => {
    if (column.backingTableColumn && !columnKeyByBacking.has(column.backingTableColumn)) {
      columnKeyByBacking.set(column.backingTableColumn, column.key);
    }
  });

  // The condition endpoint returns backend/API column names, but the table-builder store still
  // expects the legacy frontend keys it was built around. Normalize here so the rest of the UI
  // can keep using the existing condition-table model.
  const transformedHeaders = rawHeaders
    .filter((header) => !(header.name ?? '').endsWith(UOM_SUFFIX))
    .map((header) => {
      const originalName = header.name ?? '';
      let name: string;
      const mappedFromStore = columnKeyByBacking.get(originalName);

      if (mappedFromStore) {
        name = mappedFromStore;
      } else if (originalName === CONDITION_TABLE_ITEM_ID_COLUMN) {
        name = 'ID';
      } else if (
        originalName === SeeqNames.MaterializedTables.FormulaAcross.AssetColumn ||
        originalName === 'Asset.properties.name'
      ) {
        name = COLUMNS_AND_STATS.asset.key;
      } else if (originalName === SeeqNames.MaterializedTables.FormulaAcross.PathColumn) {
        name = COLUMNS_AND_STATS.fullpath.key;
      } else if (statItems.some((id) => id !== undefined && originalName.includes(id))) {
        const [id, stat] = originalName.split(' ');
        const statColumn = columns.find((column) => column.signalId === id && column.columnSuffix === stat);
        name = statColumn?.key ?? `statistics.${stat.toLowerCase()}_${id}`;
      } else {
        name = originalName.replace(ITEM_PROPERTIES_PREFIX, '');
        if (name === SeeqNames.Properties.Name) {
          name = 'nameExpression';
        } else if (name.includes(PRIORITY_COLOR_SUFFIX)) {
          name = name.replace(PRIORITY_COLOR_SUFFIX, PRIORITY_COLOR_REPLACEMENT);
        }
      }

      if (uomValues.has(originalName)) {
        const uomValue = uomValues.get(originalName)!;
        uomValues.delete(originalName);
        uomValues.set(name, uomValue);
      }

      return { ...header, name };
    });

  const mapColumnKeysToColumnIndex: Record<string, number> = {};
  transformedHeaders.forEach((header, index) => {
    mapColumnKeysToColumnIndex[header.name ?? ''] = index;
  });

  const storeColumnKeys = new Set(columns.map((column) => column.key));
  const conditionRowKeys = new Set(conditionTableRows.map((column) => column.key));
  const conditionHeaderProperty = headers.property;
  const hasVisibleNameColumn =
    NAME_ALIAS_KEYS.some((key) => conditionRowKeys.has(key)) ||
    (headers.type === TableBuilderHeaderType.CapsuleProperty &&
      typeof conditionHeaderProperty === 'string' &&
      conditionHeaderProperty.toLowerCase() === SeeqNames.Properties.Name.toLowerCase());
  const indexOfCapsuleId = _.findIndex(transformedHeaders, {
    name: SeeqNames.CapsuleProperties.CapsuleId,
  });

  const structuredRows: CapsuleFormulaTableRow[] = nonUomRows.map((row) => {
    if (indexOfCapsuleId >= 0 && !row[indexOfCapsuleId]) {
      row[indexOfCapsuleId] = generateTemporaryId();
    }

    const newRow = _.transform(
      mapColumnKeysToColumnIndex,
      (rowObject, index: number, key: string) => {
        rowObject[key] = row[index];
      },
      {} as NormalizedConditionTableRow,
    );

    NAME_ALIAS_KEYS.forEach((key) => {
      if (newRow[key] != null || !storeColumnKeys.has(key)) return;
      const sibling = NAME_ALIAS_KEYS.find((candidate) => candidate !== key && newRow[candidate] != null);
      if (sibling) {
        newRow[key] = newRow[sibling];
      }
    });

    newRow.startTime = nullableNanosToMillis(newRow[SeeqNames.CapsuleProperties.Start]);
    newRow.endTime = nullableNanosToMillis(newRow[SeeqNames.CapsuleProperties.End]);

    if (newRow.cursorKey === -1) {
      newRow.cursorKey = null;
    }

    return newRow as CapsuleFormulaTableRow;
  });

  const ignoredMergeConflictColumns = new Set<string>(['ID']);
  if (!hasVisibleNameColumn) {
    NAME_ALIAS_KEYS.forEach((key) => ignoredMergeConflictColumns.add(key));
  }
  const structuredTable = options.mergeRowsByCapsule
    ? (sortConditionRowsForDisplay(
        mergeComplementaryConditionRows(structuredRows, ignoredMergeConflictColumns),
      ) as CapsuleFormulaTableRow[])
    : structuredRows;

  const headersOutput: NormalizedConditionTableHeader[] = transformedHeaders.map((header) => ({
    name: header.name ?? '',
    type: header.type === TypeEnum.NUMERIC ? 'number' : 'string',
    units: uomValues.get(header.name ?? '') || '',
  }));

  return { headers: headersOutput, structuredTable };
};
